const COMMENT_HIGHLIGHT = "comment";
const ACTIVE_HIGHLIGHT = "comment-active";
const PENDING_HIGHLIGHT = "comment-pending";

// Read-only chapters draw inline comments with the CSS Custom Highlight API (styled in styles.css):
// ranges over the rendered text, so nothing is inserted into React-managed DOM.
type THighlightRegistry = { set(name: string, highlight: Highlight): unknown; delete(name: string): boolean };

const highlightRegistry = (): THighlightRegistry | null =>
  typeof CSS !== "undefined" && "highlights" in CSS && typeof Highlight !== "undefined"
    ? (CSS.highlights as unknown as THighlightRegistry)
    : null;

function setHighlight(name: string, ranges: Range[]) {
  const registry = highlightRegistry();
  if (!registry) return;
  if (ranges.length === 0) registry.delete(name);
  else registry.set(name, new Highlight(...ranges));
}

export const setCommentHighlights = (ranges: Range[]) => setHighlight(COMMENT_HIGHLIGHT, ranges);

export const setPendingCommentHighlight = (range: Range | null) =>
  setHighlight(PENDING_HIGHLIGHT, range ? [range] : []);

export function flashCommentHighlight(range: Range, durationMs = 1000) {
  setHighlight(ACTIVE_HIGHLIGHT, [range]);
  window.setTimeout(() => setHighlight(ACTIVE_HIGHLIGHT, []), durationMs);
}

export function scrollRangeIntoView(range: Range) {
  const { startContainer } = range;
  const element = startContainer instanceof Element ? startContainer : startContainer.parentElement;
  element?.scrollIntoView({ behavior: "smooth", block: "center" });
}

type TCaretDocument = Document & {
  caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
};

function caretAtPoint(x: number, y: number): { node: Node; offset: number } | null {
  const doc = document as TCaretDocument;
  if (doc.caretPositionFromPoint) {
    const position = doc.caretPositionFromPoint(x, y);
    return position ? { node: position.offsetNode, offset: position.offset } : null;
  }
  if (doc.caretRangeFromPoint) {
    const caret = doc.caretRangeFromPoint(x, y);
    return caret ? { node: caret.startContainer, offset: caret.startOffset } : null;
  }
  return null;
}

export function findRangeAtPoint<T extends { range: Range }>(entries: T[], x: number, y: number): T | null {
  const caret = caretAtPoint(x, y);
  if (!caret) return null;
  return (
    entries.find((entry) => {
      try {
        return entry.range.isPointInRange(caret.node, caret.offset);
      } catch {
        return false;
      }
    }) ?? null
  );
}
